import { useState, useEffect, useCallback, useRef } from 'react';
import CompareLayouts from './CompareLayouts';
import ContractorFinder from './ContractorFinder';

interface CostEstimatorProps {
  darkMode: boolean;
  floorPlanConfig: {
    bedrooms: number;
    bathrooms: number;
    livingRooms?: number;
    customRooms?: { id: string; name: string; count: number }[];
    area: number;
    unit: string;
    kitchenType: string;
    extras: string[];
  };
  selectedStyle: string;
  onPinCompare?: (pinned: boolean) => void;
  isPinned?: boolean;
}

// ── Location multiplier data ──
interface LocationData {
  label: string;
  state: string;
  multiplier: number;
  tier: 'low' | 'mid' | 'high' | 'premium';
  flag: string;
}

const LOCATIONS: LocationData[] = [
  { label: 'National Average', state: 'US', multiplier: 1.00, tier: 'mid', flag: '🇺🇸' },
  { label: 'New York City, NY', state: 'NY', multiplier: 1.68, tier: 'premium', flag: '🗽' },
  { label: 'San Francisco, CA', state: 'CA', multiplier: 1.72, tier: 'premium', flag: '🌉' },
  { label: 'Los Angeles, CA', state: 'CA', multiplier: 1.55, tier: 'premium', flag: '🌴' },
  { label: 'Seattle, WA', state: 'WA', multiplier: 1.42, tier: 'high', flag: '⛰️' },
  { label: 'Boston, MA', state: 'MA', multiplier: 1.45, tier: 'high', flag: '🦞' },
  { label: 'Chicago, IL', state: 'IL', multiplier: 1.22, tier: 'high', flag: '🌆' },
  { label: 'Miami, FL', state: 'FL', multiplier: 1.18, tier: 'high', flag: '🌊' },
  { label: 'Denver, CO', state: 'CO', multiplier: 1.15, tier: 'mid', flag: '🏔️' },
  { label: 'Austin, TX', state: 'TX', multiplier: 1.08, tier: 'mid', flag: '🤠' },
  { label: 'Dallas, TX', state: 'TX', multiplier: 1.02, tier: 'mid', flag: '⭐' },
  { label: 'Houston, TX', state: 'TX', multiplier: 0.98, tier: 'mid', flag: '🛢️' },
  { label: 'Phoenix, AZ', state: 'AZ', multiplier: 0.95, tier: 'mid', flag: '🌵' },
  { label: 'Atlanta, GA', state: 'GA', multiplier: 0.92, tier: 'low', flag: '🍑' },
  { label: 'Nashville, TN', state: 'TN', multiplier: 0.90, tier: 'low', flag: '🎸' },
  { label: 'Charlotte, NC', state: 'NC', multiplier: 0.88, tier: 'low', flag: '🏁' },
  { label: 'Columbus, OH', state: 'OH', multiplier: 0.85, tier: 'low', flag: '🌽' },
  { label: 'Indianapolis, IN', state: 'IN', multiplier: 0.82, tier: 'low', flag: '🏎️' },
  { label: 'Kansas City, MO', state: 'MO', multiplier: 0.80, tier: 'low', flag: '🎷' },
  { label: 'Memphis, TN', state: 'TN', multiplier: 0.78, tier: 'low', flag: '🎵' },
];

interface CostBreakdown {
  baseStructure: number;
  extras: number;
  stylePremium: number;
  total: number;
  totalMin: number;
  totalMax: number;
}

export default function CostEstimator({ darkMode, floorPlanConfig, selectedStyle, onPinCompare, isPinned }: CostEstimatorProps) {
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [costTier, setCostTier] = useState<'Budget' | 'Mid-Range' | 'Premium' | 'Luxury'>('Mid-Range');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [costHistory, setCostHistory] = useState<{ label: string; total: number; ts: number }[]>([]);
  const [shareToast, setShareToast] = useState('');
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  const selectedLocation = LOCATIONS[selectedLocationIdx];

  // Close location dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target as Node)) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStyleCostPerSqft = (style: string): number => {
    const styleCosts: { [key: string]: number } = {
      Minimalist: 120, Ranch: 120, Colonial: 175, Craftsman: 175, Farmhouse: 175,
      'Cape Cod': 175, Victorian: 250, Mediterranean: 250, Tudor: 250,
      Contemporary: 200, Industrial: 200, Scandinavian: 200,
      'Tiny Home': 150, Container: 140, 'A-Frame': 160, Dome: 180,
    };
    return styleCosts[style] || 175;
  };

  const getExtraCost = (extra: string): number => {
    const extraCosts: { [key: string]: number } = {
      Pool: 40000, Garage: 25000, Gym: 20000, Basement: 30000,
      Balcony: 8000, Patio: 12000, 'Home Office': 15000, 'Wine Cellar': 18000,
      'Home Theater': 35000, Sauna: 10000, Library: 12000, 'Game Room': 15000,
      Workshop: 20000, Mudroom: 5000, 'Laundry Room': 8000, 'Walk-in Closet': 6000,
      Sunroom: 22000, Greenhouse: 16000,
    };
    return extraCosts[extra] || 10000;
  };

  const getCostTier = (cpp: number): 'Budget' | 'Mid-Range' | 'Premium' | 'Luxury' => {
    if (cpp <= 140) return 'Budget';
    if (cpp <= 190) return 'Mid-Range';
    if (cpp <= 230) return 'Premium';
    return 'Luxury';
  };

  const getRoomCostMultiplier = useCallback(() => {
    const livingRooms = floorPlanConfig.livingRooms || 1;
    const multiplier = 1 + (livingRooms - 1) * 0.08;
    const customRoomCost = (floorPlanConfig.customRooms || []).reduce((sum, r) => {
      return sum + (getExtraCost(r.name) || 15000) * r.count;
    }, 0);
    return { multiplier, customRoomCost };
  }, [floorPlanConfig]);

  const calculateCost = useCallback(() => {
    setIsCalculating(true);
    setTimeout(() => {
      const area = floorPlanConfig.unit === 'sqm' ? floorPlanConfig.area * 10.764 : floorPlanConfig.area;
      const costPerSqft = getStyleCostPerSqft(selectedStyle);
      const locMultiplier = selectedLocation.multiplier;
      const baseStructure = Math.round(area * costPerSqft * locMultiplier);
      let extrasTotal = 0;
      floorPlanConfig.extras.forEach(e => { extrasTotal += Math.round(getExtraCost(e) * locMultiplier); });
      const { multiplier, customRoomCost } = getRoomCostMultiplier();
      const adjustedBase = Math.round(baseStructure * multiplier);
      const adjustedCustomRoomCost = Math.round(customRoomCost * locMultiplier);
      const stylePremium = selectedStyle ? Math.round(adjustedBase * 0.08) : 0;
      const total = adjustedBase + extrasTotal + adjustedCustomRoomCost + stylePremium;
      setCostBreakdown({
        baseStructure: adjustedBase,
        extras: extrasTotal + adjustedCustomRoomCost,
        stylePremium,
        total,
        totalMin: Math.round(total * 0.85),
        totalMax: Math.round(total * 1.15),
      });
      setCostTier(getCostTier(costPerSqft));
      // Track history — keep last 8 entries, deduplicate consecutive same values
      const histLabel = `${floorPlanConfig.bedrooms}bd/${floorPlanConfig.bathrooms}ba · ${floorPlanConfig.area}${floorPlanConfig.unit}`;
      setCostHistory(prev => {
        const last = prev[prev.length - 1];
        if (last && last.total === total) return prev;
        const entry = { label: histLabel, total, ts: Date.now() };
        return [...prev.slice(-7), entry];
      });
      setIsCalculating(false);
    }, 1200);
  }, [floorPlanConfig, selectedStyle, getRoomCostMultiplier, selectedLocation]);

  useEffect(() => { calculateCost(); }, [calculateCost]);

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  // Total room count for cost-per-room metric
  const totalRoomCount =
    (floorPlanConfig.livingRooms || 1) +
    floorPlanConfig.bedrooms +
    floorPlanConfig.bathrooms +
    1 + // kitchen
    (floorPlanConfig.customRooms || []).reduce((s, r) => s + r.count, 0);

  const costPerRoom = costBreakdown ? Math.round(costBreakdown.total / totalRoomCount) : 0;

  // ── Export to PDF ──
  const handleExportPDF = useCallback(() => {
    if (!costBreakdown) return;
    setIsExporting(true);

    const customRoomLines = (floorPlanConfig.customRooms || [])
      .map(r => `<tr><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:#6b7280"><span style="color:#ec4899">&#9632;</span> ${r.name}${r.count > 1 ? ` ×${r.count}` : ''}</td><td style="text-align:right;font-weight:600;color:#ec4899">+${fmt(getExtraCost(r.name) * r.count)}</td></tr>`)
      .join('');

    const extraLines = floorPlanConfig.extras
      .map(e => `<tr><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:#6b7280"><span style="color:#14b8a6">&#10003;</span> ${e}</td><td style="text-align:right;font-weight:600;color:#14b8a6">+${fmt(getExtraCost(e))}</td></tr>`)
      .join('');

    const livingExtra = (floorPlanConfig.livingRooms || 1) > 1
      ? `<tr><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:#6b7280"><span style="color:#14b8a6">&#9632;</span> Extra Living Rooms ×${(floorPlanConfig.livingRooms || 1) - 1}</td><td style="text-align:right;font-weight:600;color:#14b8a6">+${Math.round(((floorPlanConfig.livingRooms || 1) - 1) * 0.08 * 100)}% base</td></tr>`
      : '';

    const locationLine = selectedLocation.state !== 'US'
      ? `<div style="margin-top:6px;font-size:12px;color:#6b7280">${selectedLocation.flag} ${selectedLocation.label} · ×${selectedLocation.multiplier.toFixed(2)} regional multiplier</div>`
      : '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Cost Estimate — Master Architect</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111827; background: #fff; padding: 40px; max-width: 720px; margin: 0 auto; }
    .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #14b8a6; }
    .logo { display: flex; align-items: center; gap: 12px; }
    .logo-icon { width: 44px; height: 44px; background: linear-gradient(135deg,#14b8a6,#06b6d4); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; font-size: 22px; font-weight: bold; }
    .logo-text h1 { font-size: 18px; font-weight: 800; color: #111827; }
    .logo-text p { font-size: 12px; color: #6b7280; margin-top: 2px; }
    .date { font-size: 12px; color: #9ca3af; }
    .section { margin-bottom: 24px; }
    .section-title { font-size: 13px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #f3f4f6; }
    .hero { background: linear-gradient(135deg,#14b8a6,#06b6d4); border-radius: 14px; padding: 24px; color: white; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: center; }
    .hero-left h2 { font-size: 13px; opacity: 0.85; margin-bottom: 4px; }
    .hero-left .tier { font-size: 26px; font-weight: 800; }
    .hero-right { text-align: right; }
    .hero-right .label { font-size: 12px; opacity: 0.85; }
    .hero-right .total { font-size: 30px; font-weight: 800; }
    .metrics { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; margin-bottom: 24px; }
    .metric { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; text-align: center; }
    .metric .val { font-size: 20px; font-weight: 800; color: #14b8a6; }
    .metric .lbl { font-size: 11px; color: #6b7280; margin-top: 3px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    tr td { padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
    tr td:last-child { text-align: right; }
    .total-row td { font-size: 15px; font-weight: 800; padding-top: 14px; border-bottom: none; }
    .range-row td { font-size: 12px; color: #6b7280; border-bottom: none; padding-top: 4px; }
    .room-pills { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .pill { background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 20px; padding: 4px 10px; font-size: 11px; color: #0f766e; font-weight: 600; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; line-height: 1.6; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">
      <div class="logo-icon">M</div>
      <div class="logo-text">
        <h1>Master Architect</h1>
        <p>AI Cost Estimate Report</p>
      </div>
    </div>
    <div style="text-align:right">
      <div class="date">Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      ${locationLine}
    </div>
  </div>

  <div class="hero">
    <div class="hero-left">
      <h2>Cost Tier</h2>
      <div class="tier">${costTier}</div>
    </div>
    <div class="hero-right">
      <div class="label">Estimated Total</div>
      <div class="total">${fmt(costBreakdown.total)}</div>
    </div>
  </div>

  <div class="metrics">
    <div class="metric">
      <div class="val">${fmt(costBreakdown.totalMin)}</div>
      <div class="lbl">Low Estimate</div>
    </div>
    <div class="metric">
      <div class="val">${fmt(costPerRoom)}</div>
      <div class="lbl">Cost per Room</div>
    </div>
    <div class="metric">
      <div class="val">${fmt(costBreakdown.totalMax)}</div>
      <div class="lbl">High Estimate</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Room Configuration</div>
    <div class="room-pills">
      <span class="pill">${floorPlanConfig.livingRooms || 1} Living Room${(floorPlanConfig.livingRooms || 1) > 1 ? 's' : ''}</span>
      <span class="pill">${floorPlanConfig.bedrooms} Bedroom${floorPlanConfig.bedrooms > 1 ? 's' : ''}</span>
      <span class="pill">${floorPlanConfig.bathrooms} Bathroom${floorPlanConfig.bathrooms > 1 ? 's' : ''}</span>
      <span class="pill">1 Kitchen (${floorPlanConfig.kitchenType})</span>
      ${(floorPlanConfig.customRooms || []).map(r => `<span class="pill">${r.count > 1 ? r.count + '× ' : ''}${r.name}</span>`).join('')}
      <span class="pill">${floorPlanConfig.area} ${floorPlanConfig.unit} total</span>
      ${selectedStyle ? `<span class="pill">${selectedStyle} style</span>` : ''}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Cost Breakdown</div>
    <table>
      <tr>
        <td style="color:#374151;font-weight:600">Base Structure</td>
        <td style="font-weight:700">${fmt(costBreakdown.baseStructure)}</td>
      </tr>
      ${livingExtra}
      ${customRoomLines}
      ${extraLines}
      ${costBreakdown.stylePremium > 0 ? `<tr><td style="padding:6px 0;border-bottom:1px solid #e5e7eb;color:#6b7280">Style Premium (${selectedStyle})</td><td style="text-align:right;font-weight:600;color:#8b5cf6">+${fmt(costBreakdown.stylePremium)}</td></tr>` : ''}
      <tr class="total-row">
        <td>Total Estimate</td>
        <td style="color:#14b8a6">${fmt(costBreakdown.total)}</td>
      </tr>
      <tr class="range-row">
        <td>Estimated Range (±15%)</td>
        <td>${fmt(costBreakdown.totalMin)} – ${fmt(costBreakdown.totalMax)}</td>
      </tr>
    </table>
  </div>

  <div class="footer">
    <strong>Disclaimer:</strong> This is an AI-generated estimate based on average US construction costs. Actual costs may vary significantly based on location, materials, labor rates, site conditions, and market conditions. Always consult licensed contractors for accurate quotes. Generated by Master Architect · 1world-ai.com
  </div>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '800px';
    iframe.style.height = '1100px';
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          printFrameRef.current = null;
          setIsExporting(false);
        }, 1000);
      }, 600);
    } else {
      setIsExporting(false);
    }
  }, [costBreakdown, costTier, costPerRoom, floorPlanConfig, selectedStyle]);

  // ── Share Estimate ──
  const handleShareEstimate = useCallback(() => {
    if (!costBreakdown) return;
    const payload = {
      total: costBreakdown.total,
      totalMin: costBreakdown.totalMin,
      totalMax: costBreakdown.totalMax,
      tier: costTier,
      location: selectedLocation.label,
      bedrooms: floorPlanConfig.bedrooms,
      bathrooms: floorPlanConfig.bathrooms,
      area: floorPlanConfig.area,
      unit: floorPlanConfig.unit,
      style: selectedStyle,
    };
    const encoded = btoa(encodeURIComponent(JSON.stringify(payload)));
    const url = `${window.location.origin}${window.location.pathname}?estimate=${encoded}`;
    navigator.clipboard.writeText(url).then(() => {
      setShareToast('Estimate link copied to clipboard!');
      setTimeout(() => setShareToast(''), 2500);
    }).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setShareToast('Estimate link copied!');
      setTimeout(() => setShareToast(''), 2500);
    });
  }, [costBreakdown, costTier, selectedLocation, floorPlanConfig, selectedStyle]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Budget': return 'from-emerald-500 to-green-600';
      case 'Mid-Range': return 'from-teal-500 to-cyan-600';
      case 'Premium': return 'from-violet-500 to-pink-600';
      case 'Luxury': return 'from-amber-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Budget': return 'ri-coin-line';
      case 'Mid-Range': return 'ri-money-dollar-circle-line';
      case 'Premium': return 'ri-vip-crown-line';
      case 'Luxury': return 'ri-vip-diamond-line';
      default: return 'ri-money-dollar-circle-line';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className={`text-lg font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI Cost Estimator</h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estimated build cost based on your design</p>
        </div>
        <div className="flex items-center gap-2">
          {onPinCompare && (
            <button
              onClick={() => onPinCompare(!isPinned)}
              title={isPinned ? 'Unpin Compare panel' : 'Pin Compare panel as floating overlay'}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isPinned
                  ? darkMode ? 'bg-teal-700 text-teal-200 border border-teal-600' : 'bg-teal-100 text-teal-700 border border-teal-300'
                  : darkMode ? 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className={isPinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'}></i>
              <span className="text-xs">{isPinned ? 'Pinned' : 'Pin Compare'}</span>
            </button>
          )}
          {costBreakdown && !isCalculating && (
            <>
              <button
                onClick={handleShareEstimate}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${darkMode ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <i className="ri-share-line text-teal-500"></i>
                <span className="text-xs">Share</span>
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isExporting
                    ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : darkMode
                    ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {isExporting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Preparing…</>
                ) : (
                  <><i className="ri-file-pdf-line text-rose-500"></i>PDF</>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Location Multiplier Selector ── */}
      <div ref={locationDropdownRef} className="relative">
        <div className={`rounded-xl border p-3 ${darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <i className={`ri-map-pin-2-line text-sm ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
              <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location Multiplier</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                selectedLocation.tier === 'premium' ? darkMode ? 'bg-rose-900/30 text-rose-400' : 'bg-rose-100 text-rose-700' :
                selectedLocation.tier === 'high' ? darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700' :
                selectedLocation.tier === 'mid' ? darkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700' :
                darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
              }`}>
                ×{selectedLocation.multiplier.toFixed(2)}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowLocationDropdown(v => !v)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
              showLocationDropdown
                ? darkMode ? 'border-teal-500 bg-teal-900/20' : 'border-teal-400 bg-teal-50'
                : darkMode ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-300 bg-white hover:border-gray-400'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-base">{selectedLocation.flag}</span>
              <div className="text-left">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedLocation.label}</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedLocation.multiplier > 1
                    ? `+${Math.round((selectedLocation.multiplier - 1) * 100)}% above national avg`
                    : selectedLocation.multiplier < 1
                    ? `${Math.round((1 - selectedLocation.multiplier) * 100)}% below national avg`
                    : 'National average baseline'}
                </div>
              </div>
            </div>
            <i className={`${showLocationDropdown ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
          </button>

          {/* Dropdown list */}
          {showLocationDropdown && (
            <div className={`absolute left-0 right-0 top-full mt-1 z-30 rounded-xl border shadow-xl overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
              <div className="max-h-64 overflow-y-auto">
                {LOCATIONS.map((loc, idx) => (
                  <button
                    key={loc.label}
                    onClick={() => { setSelectedLocationIdx(idx); setShowLocationDropdown(false); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors cursor-pointer ${
                      idx === selectedLocationIdx
                        ? darkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'
                        : darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-sm">{loc.flag}</span>
                      <span className="text-sm font-medium">{loc.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${
                        loc.tier === 'premium' ? 'text-rose-500' :
                        loc.tier === 'high' ? 'text-amber-500' :
                        loc.tier === 'mid' ? 'text-teal-500' :
                        'text-emerald-500'
                      }`}>×{loc.multiplier.toFixed(2)}</span>
                      {idx === selectedLocationIdx && <i className="ri-check-line text-teal-500 text-sm"></i>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cost Tier Banner */}
      {costBreakdown && (
        <div className={`p-4 rounded-xl bg-gradient-to-r ${getTierColor(costTier)} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <i className={`${getTierIcon(costTier)} text-2xl`}></i>
              </div>
              <div>
                <div className="text-xs font-medium opacity-90">Cost Tier</div>
                <div className="text-xl font-bold">{costTier}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-medium opacity-90">Est. Total</div>
              <div className="text-2xl font-bold">{fmt(costBreakdown.total)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cost per Room metric card ── */}
      {costBreakdown && !isCalculating && (
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: 'Cost / Room',
              value: fmt(costPerRoom),
              sub: `${totalRoomCount} rooms total`,
              icon: 'ri-home-line',
              color: darkMode ? 'text-teal-400' : 'text-teal-600',
              bg: darkMode ? 'bg-teal-900/20 border-teal-700/40' : 'bg-teal-50 border-teal-200',
            },
            {
              label: 'Low Estimate',
              value: fmt(costBreakdown.totalMin),
              sub: '−15% variance',
              icon: 'ri-arrow-down-line',
              color: darkMode ? 'text-emerald-400' : 'text-emerald-600',
              bg: darkMode ? 'bg-emerald-900/20 border-emerald-700/40' : 'bg-emerald-50 border-emerald-200',
            },
            {
              label: 'High Estimate',
              value: fmt(costBreakdown.totalMax),
              sub: '+15% variance',
              icon: 'ri-arrow-up-line',
              color: darkMode ? 'text-amber-400' : 'text-amber-600',
              bg: darkMode ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200',
            },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-3 ${m.bg}`}>
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg mb-2 ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <i className={`${m.icon} text-sm ${m.color}`}></i>
              </div>
              <div className={`text-base font-bold leading-tight ${m.color}`}>{m.value}</div>
              <div className={`text-xs font-semibold mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{m.label}</div>
              <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{m.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Cost Breakdown */}
      {costBreakdown && !isCalculating && (
        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-5 space-y-4`}>
          <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Cost Breakdown</h4>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                <i className="ri-building-line text-teal-500"></i>
              </div>
              <div>
                <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Base Structure</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {floorPlanConfig.area} {floorPlanConfig.unit} × {fmt(getStyleCostPerSqft(selectedStyle))}/{floorPlanConfig.unit === 'sqm' ? 'sqm' : 'sqft'} × {selectedLocation.multiplier.toFixed(2)} ({selectedLocation.state})
                </div>
              </div>
            </div>
            <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmt(costBreakdown.baseStructure)}</div>
          </div>

          {costBreakdown.extras > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                  <i className="ri-add-box-line text-teal-500"></i>
                </div>
                <div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Extras & Custom Rooms</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {floorPlanConfig.extras.length + (floorPlanConfig.customRooms || []).length} items
                  </div>
                </div>
              </div>
              <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmt(costBreakdown.extras)}</div>
            </div>
          )}

          {costBreakdown.stylePremium > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-white'}`}>
                  <i className="ri-palette-line text-violet-500"></i>
                </div>
                <div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Style Premium</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedStyle || 'No style'}</div>
                </div>
              </div>
              <div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmt(costBreakdown.stylePremium)}</div>
            </div>
          )}

          <div className={`border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'} pt-4`}>
            <div className="flex items-center justify-between">
              <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Estimated Range</div>
              <div className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {fmt(costBreakdown.totalMin)} – {fmt(costBreakdown.totalMax)}
              </div>
            </div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-right mt-1`}>±15% variance</div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isCalculating && (
        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-8 flex flex-col items-center justify-center space-y-4`}>
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-teal-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-teal-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Calculating costs…</div>
        </div>
      )}

      {/* Per-Room & Per-Extra Cost Breakdown */}
      {costBreakdown && !isCalculating && (
        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-5`}>
          <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 flex items-center gap-2`}>
            <i className="ri-list-check-2 text-teal-500"></i>
            Per-Room Cost Breakdown
          </h4>
          <div className="space-y-0">
            {(floorPlanConfig.livingRooms || 1) > 1 && (
              <div className={`flex items-center justify-between py-2 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <i className="ri-sofa-line text-teal-500 text-sm"></i>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Extra Living Rooms ×{(floorPlanConfig.livingRooms || 1) - 1}
                  </span>
                </div>
                <span className={`text-xs font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                  +{Math.round(((floorPlanConfig.livingRooms || 1) - 1) * 0.08 * 100)}% base
                </span>
              </div>
            )}
            {(floorPlanConfig.customRooms || []).map((room) => (
              <div key={room.id} className={`flex items-center justify-between py-2 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <i className="ri-home-gear-line text-pink-500 text-sm"></i>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {room.name}{room.count > 1 ? ` ×${room.count}` : ''}
                  </span>
                </div>
                <span className={`text-xs font-semibold ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                  +{fmt(getExtraCost(room.name) * room.count)}
                </span>
              </div>
            ))}
            {floorPlanConfig.extras.map((extra, i) => (
              <div key={i} className={`flex items-center justify-between py-2 ${i < floorPlanConfig.extras.length - 1 ? `border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}` : ''}`}>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-sm"></i>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{extra}</span>
                </div>
                <span className={`text-xs font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                  +{fmt(getExtraCost(extra))}
                </span>
              </div>
            ))}
            {(floorPlanConfig.livingRooms || 1) <= 1 &&
              (floorPlanConfig.customRooms || []).length === 0 &&
              floorPlanConfig.extras.length === 0 && (
              <p className={`text-xs text-center py-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No custom rooms or extras added yet
              </p>
            )}
          </div>
        </div>
      )}

      {/* Recalculate */}
      <button
        onClick={calculateCost}
        disabled={isCalculating}
        className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap ${
          isCalculating
            ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white hover:shadow-lg'
        }`}
      >
        {isCalculating ? <><i className="ri-loader-4-line animate-spin"></i>Calculating…</> : <><i className="ri-refresh-line"></i>Recalculate Estimate</>}
      </button>

      {/* Disclaimer */}
      <div className={`${darkMode ? 'bg-amber-900/20 border-amber-700/30' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4`}>
        <div className="flex items-start gap-3">
          <i className={`ri-information-line text-lg ${darkMode ? 'text-amber-400' : 'text-amber-600'} mt-0.5`}></i>
          <div>
            <h5 className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-800'} mb-1`}>AI-Generated Estimate</h5>
            <p className={`text-xs ${darkMode ? 'text-amber-400/80' : 'text-amber-700'} leading-relaxed`}>
              Approximate cost based on average US construction costs. Actual costs vary by location, materials, labor, and market conditions. Consult local contractors for accurate quotes.
            </p>
          </div>
        </div>
      </div>

      {/* Room Config Summary */}
      {((floorPlanConfig.livingRooms || 1) > 1 || (floorPlanConfig.customRooms || []).length > 0) && (
        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-5`}>
          <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Room Configuration</h4>
          <div className="space-y-2">
            {[
              { icon: 'ri-sofa-line', color: 'text-teal-500', label: 'Living / Lounge Rooms', val: floorPlanConfig.livingRooms || 1 },
              { icon: 'ri-hotel-bed-line', color: 'text-violet-500', label: 'Bedrooms', val: floorPlanConfig.bedrooms },
              { icon: 'ri-water-flash-line', color: 'text-cyan-500', label: 'Bathrooms', val: floorPlanConfig.bathrooms },
            ].map(r => (
              <div key={r.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className={`${r.icon} ${r.color} text-sm`}></i>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.label}</span>
                </div>
                <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{r.val}</span>
              </div>
            ))}
            {(floorPlanConfig.customRooms || []).map(room => (
              <div key={room.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-home-gear-line text-pink-500 text-sm"></i>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{room.name}</span>
                </div>
                <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{room.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost Factors */}
      <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-5`}>
        <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Cost Factors Included</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: 'ri-hammer-line', label: 'Foundation' },
            { icon: 'ri-building-line', label: 'Framing' },
            { icon: 'ri-plug-line', label: 'Electrical' },
            { icon: 'ri-drop-line', label: 'Plumbing' },
            { icon: 'ri-temp-hot-line', label: 'HVAC' },
            { icon: 'ri-paint-brush-line', label: 'Finishes' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <i className={`${f.icon} text-teal-500 text-sm`}></i>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Share toast */}
      {shareToast && (
        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold ${darkMode ? 'bg-teal-900/30 text-teal-300 border border-teal-700/40' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
          <i className="ri-checkbox-circle-fill text-teal-500"></i>
          {shareToast}
        </div>
      )}

      {/* ── Cost History Chart ── */}
      {costHistory.length >= 2 && (
        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center gap-2`}>
              <i className="ri-line-chart-line text-teal-500"></i>
              Cost History
            </h4>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{costHistory.length} snapshots</span>
              <button
                onClick={() => setCostHistory([])}
                className={`text-xs cursor-pointer ${darkMode ? 'text-gray-500 hover:text-rose-400' : 'text-gray-400 hover:text-rose-500'}`}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Mini bar chart */}
          {(() => {
            const maxVal = Math.max(...costHistory.map(h => h.total));
            const minVal = Math.min(...costHistory.map(h => h.total));
            const range = maxVal - minVal || 1;
            const fmt2 = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}k`;
            return (
              <div className="space-y-3">
                {/* SVG sparkline */}
                <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-800/60' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <svg viewBox={`0 0 ${costHistory.length * 40} 60`} className="w-full h-16" preserveAspectRatio="none">
                    {/* Grid lines */}
                    {[0, 0.5, 1].map(pct => (
                      <line
                        key={pct}
                        x1="0" y1={60 - pct * 50}
                        x2={costHistory.length * 40} y2={60 - pct * 50}
                        stroke={darkMode ? '#374151' : '#f3f4f6'}
                        strokeWidth="1"
                      />
                    ))}
                    {/* Area fill */}
                    <defs>
                      <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>
                    <polygon
                      points={[
                        ...costHistory.map((h, i) => `${i * 40 + 20},${60 - ((h.total - minVal) / range) * 50}`),
                        `${(costHistory.length - 1) * 40 + 20},60`,
                        `20,60`,
                      ].join(' ')}
                      fill="url(#histGrad)"
                    />
                    {/* Line */}
                    <polyline
                      points={costHistory.map((h, i) => `${i * 40 + 20},${60 - ((h.total - minVal) / range) * 50}`).join(' ')}
                      fill="none"
                      stroke="#14b8a6"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    {/* Dots */}
                    {costHistory.map((h, i) => {
                      const x = i * 40 + 20;
                      const y = 60 - ((h.total - minVal) / range) * 50;
                      const isLast = i === costHistory.length - 1;
                      return (
                        <circle
                          key={i}
                          cx={x} cy={y} r={isLast ? 4 : 3}
                          fill={isLast ? '#14b8a6' : darkMode ? '#1f2937' : '#fff'}
                          stroke="#14b8a6"
                          strokeWidth="2"
                        />
                      );
                    })}
                  </svg>
                </div>

                {/* History list — last 4 */}
                <div className="space-y-1.5">
                  {[...costHistory].reverse().slice(0, 4).map((h, i) => {
                    const isLatest = i === 0;
                    const prev = [...costHistory].reverse()[i + 1];
                    const delta = prev ? h.total - prev.total : 0;
                    return (
                      <div key={h.ts} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isLatest ? darkMode ? 'bg-teal-900/20 border border-teal-700/40' : 'bg-teal-50 border border-teal-200' : darkMode ? 'bg-gray-800/40' : 'bg-white border border-gray-100'}`}>
                        <div className="flex items-center gap-2">
                          {isLatest && <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${darkMode ? 'bg-teal-700 text-teal-200' : 'bg-teal-500 text-white'}`}>Now</span>}
                          <span className={`text-xs ${isLatest ? darkMode ? 'text-teal-300' : 'text-teal-700' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {delta !== 0 && (
                            <span className={`text-xs font-semibold ${delta > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                              {delta > 0 ? '+' : ''}{fmt2(delta)}
                            </span>
                          )}
                          <span className={`text-xs font-bold ${isLatest ? darkMode ? 'text-teal-300' : 'text-teal-700' : darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{fmt2(h.total)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Min/Max summary */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-arrow-down-line text-emerald-500 text-xs"></i>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lowest: <strong className="text-emerald-500">{fmt2(minVal)}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Highest: <strong className="text-rose-500">{fmt2(maxVal)}</strong></span>
                    <i className="ri-arrow-up-line text-rose-500 text-xs"></i>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Contractor Finder ── */}
      <ContractorFinder
        darkMode={darkMode}
        locationLabel={selectedLocation.label}
        locationState={selectedLocation.state}
        estimatedTotal={costBreakdown?.total ?? null}
      />

      {/* Compare Layouts */}
      {!isPinned && (
        <CompareLayouts
          darkMode={darkMode}
          currentConfig={floorPlanConfig}
          selectedStyle={selectedStyle}
          locationMultiplier={selectedLocation.multiplier}
          onPinToggle={onPinCompare ? () => onPinCompare(true) : undefined}
        />
      )}
      {isPinned && (
        <div className={`rounded-xl border-2 border-dashed p-4 text-center ${darkMode ? 'border-teal-700/50 bg-teal-900/10' : 'border-teal-300 bg-teal-50/50'}`}>
          <i className={`ri-pushpin-fill text-2xl mb-2 block ${darkMode ? 'text-teal-400' : 'text-teal-500'}`}></i>
          <p className={`text-sm font-semibold ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>Compare panel is pinned</p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>It's floating on the right side — switch to any tab to use it</p>
          <button
            onClick={() => onPinCompare && onPinCompare(false)}
            className={`mt-3 px-4 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all ${darkMode ? 'bg-teal-800 text-teal-200 hover:bg-teal-700' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}
          >
            <i className="ri-pushpin-line mr-1"></i>Unpin
          </button>
        </div>
      )}
    </div>
  );
}
